import { createTheme } from '@mui/material/styles'
import type { PaletteColor, PaletteColorOptions } from '@mui/material/styles'
import type { CSSObject } from '@emotion/react'
import { colors } from './colors'
import { color, typography as figType, type TypeStyle } from './tokens'
import { semantic } from './semantic'

declare module '@mui/material/styles' {
    interface TypographyVariants {
        body3: CSSObject
        body4: CSSObject
        body5: CSSObject
        link: CSSObject
        link2: CSSObject
    }

    interface TypographyVariantsOptions {
        body3?: CSSObject
        body4?: CSSObject
        body5?: CSSObject
        link?: CSSObject
        link2?: CSSObject
    }

    interface Palette {
        background1: PaletteColor
        background2: PaletteColor
        background3: PaletteColor
        background4: PaletteColor
        stroke1: PaletteColor
        stroke2: PaletteColor
        muted: PaletteColor
    }

    interface PaletteOptions {
        background1?: PaletteColorOptions
        background2?: PaletteColorOptions
        background3?: PaletteColorOptions
        background4?: PaletteColorOptions
        stroke1?: PaletteColorOptions
        stroke2?: PaletteColorOptions
        muted?: PaletteColorOptions
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        body3: true
        body4: true
        body5: true
        link: true
        link2: true
    }
}

const FONT_INTER = "'Inter', 'Noto Sans Thai', sans-serif"

// Map a Figma text style (tokens.ts) → MUI typography variant CSS, verbatim.
// px units keep the exact Figma size / line-height / letter-spacing.
const type = (s: TypeStyle) => ({
    fontFamily: FONT_INTER,
    fontWeight: s.fontWeight,
    fontSize: `${s.fontSize}px`,
    lineHeight: `${s.lineHeight}px`,
    letterSpacing: `${s.letterSpacing}px`
})

export const feelGreatTheme = createTheme({
    palette: {
        // ── Semantic slots — bound to Figma semantic tokens (node 94:3) ──
        // light/dark not defined by Figma → left for MUI to derive from main.
        primary: {
            main: semantic.brand.default, // blue/600 #003b6f
            light: color.blue[300], // #5a8fc3
            dark: semantic.brand.hover, // blue/700 #002d57
            contrastText: semantic.text.inverse
        },
        error: {
            main: semantic.status.danger, // red/600 #e83a5f
            light: color.red[100], // #ffe7eb
            contrastText: semantic.text.inverse
        },
        warning: {
            main: semantic.status.warning, // orange/600 #e86900
            light: color.orange[100], // #fff1dc
            contrastText: semantic.text.inverse
        },
        success: {
            main: semantic.status.success, // green/600 #16a34a
            contrastText: semantic.text.inverse
        },
        info: {
            main: semantic.status.info, // cyan/600 #0284c7
            contrastText: semantic.text.inverse
        },
        text: {
            primary: semantic.text.primary, // gray/900 #1a202c
            secondary: semantic.text.secondary, // gray/600 #6b8cae
            disabled: semantic.text.disabled // gray/400 #ccdeef
        },
        background: {
            default: semantic.surface.page, // gray/50  #f8fafc
            paper: semantic.surface.default // gray/100 #f4f8fb
        },
        divider: semantic.border.default, // gray/300 #e2e8f0

        // ── App-specific slots — Figma has no semantic token for these; kept
        //    as-is (see colors.ts). Reconcile once Figma defines them. ──
        secondary: {
            main: colors.secondary,
            light: colors.secondaryLight,
            dark: colors.secondaryDark,
            contrastText: '#FFFFFF'
        },
        background1: { main: colors.background1, light: '#D2D9E1', dark: '#D4E7F5', contrastText: '#000000' },
        background2: { main: colors.background2, light: '#F1F5F9', dark: '#CBD5E1', contrastText: '#000000' },
        background3: { main: colors.background3, light: '#F8F9FA', dark: '#E9ECEF', contrastText: '#000000' },
        background4: { main: colors.background4, light: '#FAFCFE', dark: '#E6F2F8', contrastText: '#000000' },
        stroke1: { main: colors.stroke1, light: '#E6F1F8', dark: '#B3CEE6', contrastText: '#000000' },
        stroke2: { main: colors.stroke2, light: '#CCDDEE', dark: '#7AA8D6', contrastText: '#000000' },
        muted: { main: colors.textMuted, light: '#62748E', dark: '#CBD5E1', contrastText: '#000000' }
    },

    // ── Typography — MUI standard variants mapped to the Figma type scale ──
    // (tokens.ts). Values verbatim from Figma; assignment follows size cascade.
    typography: {
        fontFamily: FONT_INTER,

        h1: type(figType.headline.h1), // 32 / 44 · 700
        h2: type(figType.headline.h2), // 24 / 32 · 700
        h3: type(figType.headline.h3), // 20 / 28 · 600
        h4: type(figType.headline.h4), // 16 / 24 · 600
        h5: type(figType.headline.h5), // 14 / 20 · 600
        h6: type(figType.label.l2), // 16 / 24 · 600
        subtitle1: type(figType.label.l2), // 16 / 24 · 600
        subtitle2: type(figType.label.l3), // 14 / 20 · 500
        body1: type(figType.body.b2), // 16 / 24 · 400
        body2: type(figType.body.b3), // 14 / 20 · 400
        caption: type(figType.body.b4), // 12 / 16 · 400
        overline: type(figType.label.l5), // 10 / 14 · 500
        button: type(figType.label.l3), // 14 / 20 · 500

        // App-specific extra variants (not in the Figma 15-style set) — unchanged.
        body3: {
            fontFamily: FONT_INTER,
            fontSize: '16px',
            fontWeight: 400,
            color: colors.text,
            '@media (max-width:430px)': {
                fontSize: '14px'
            }
        },
        body4: {
            fontFamily: FONT_INTER,
            fontSize: '18px',
            fontWeight: 400,
            color: colors.text,
            '@media (min-width:768px)': {
                fontSize: '20px'
            }
        },
        body5: {
            fontFamily: FONT_INTER,
            fontSize: '16px',
            fontWeight: 400,
            color: colors.text
        },
        link: {
            fontFamily: FONT_INTER,
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '24px',
            color: colors.text,
            textDecoration: 'underline'
        },
        link2: {
            fontFamily: FONT_INTER,
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: '28px',
            color: colors.text,
            textDecoration: 'underline'
        }
    },

    shape: {
        // radius/8 (Figma Design Tokens · Radius)
        borderRadius: 8
    },

    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    margin: 0,
                    overflowX: 'hidden',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale'
                },
                '*': {
                    WebkitTapHighlightColor: 'transparent'
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                    borderRadius: '12px',
                    backgroundColor: '#FFFFFF',
                    padding: '0px'
                }
            }
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    '&.small-card': { padding: '16px' },
                    '&.medium-card': { padding: '24px' }
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontFamily: FONT_INTER,
                    fontWeight: 600,
                    textTransform: 'none' as const,
                    borderRadius: '30px'
                },
                containedPrimary: {
                    backgroundColor: colors.button,
                    '&:hover': {
                        backgroundColor: colors.buttonHover
                    }
                }
            }
        },
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    fontFamily: FONT_INTER,
                    '&.Mui-disabled': {
                        // Disabled buttons: surface #ccdeef, text white (all buttons app-wide).
                        cursor: 'not-allowed',
                        backgroundColor: '#ccdeef!important',
                        color: '#ffffff!important'
                    }
                }
            }
        },
        MuiCheckbox: {
            defaultProps: {
                disableRipple: true,
                disableFocusRipple: true,
                disableTouchRipple: true
            },
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent!important',
                    '&:hover, &:active, &.Mui-focusVisible, &.Mui-disabled': {
                        backgroundColor: 'transparent!important'
                    },
                    '&.Mui-disabled': {
                        color: `${colors.primaryLight}!important`
                    },
                    '& .MuiTouchRipple-root': {
                        display: 'none'
                    }
                }
            }
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontFamily: FONT_INTER,
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20px',
                    color: colors.primaryLight
                }
            }
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: '8px'
                },
                standardError: {
                    backgroundColor: colors.errorLight,
                    color: colors.error,
                    '& .MuiAlert-message': {
                        width: '100%',
                        textAlign: 'center',
                        padding: 0
                    },
                    '& .MuiAlert-icon': {
                        display: 'none'
                    }
                }
            }
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    '&::before': {
                        backgroundColor: 'transparent'
                    },
                    '&.Mui-expanded': {
                        margin: 0
                    }
                }
            }
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    padding: 0,
                    minHeight: 'auto',
                    '&.Mui-expanded': {
                        minHeight: 'auto'
                    }
                },
                content: {
                    padding: 0,
                    margin: 0,
                    '&.Mui-expanded': {
                        margin: 0
                    }
                }
            }
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    padding: 0
                }
            }
        }
    }
})

export { colors }
